import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { formatCurrency, formatDate, formatDateOnly } from "@/lib/cn";
import { Badge } from "@/components/Badge";
import { QueryError } from "@/components/QueryError";

interface Fee {
  id: string;
  amount: string;
  dueDate: string;
  period: string;
  status: "PENDING" | "PAID" | "OVERDUE" | "PARTIAL";
  paidAt?: string | null;
  paymentMethod?: string | null;
  /** When the administration (or the automatic reminder) last emailed about this fee. */
  lastReminderAt?: string | null;
  lateFeeAmount: number;
  lateFeeRate: number;
  amountPaid: number;
  balanceRemaining: number;
  totalDue: number;
}

const statusVariant: Record<string, "default" | "info" | "warning" | "success" | "danger"> = {
  PENDING: "info",
  PAID: "success",
  OVERDUE: "danger",
  PARTIAL: "warning",
};

export function ResidentFeesPage() {
  const { t, i18n } = useTranslation();
  const { data: fees = [], isLoading, isError } = useQuery({
    queryKey: ["my-fees"],
    queryFn: async () => (await api.get<Fee[]>("/maintenance-fees")).data,
  });

  // Outstanding balance includes any applicable late charge on overdue fees
  const outstanding = fees.filter((f) => f.status !== "PAID").reduce((s, f) => s + f.totalDue, 0);
  const paid = fees.filter((f) => f.status === "PAID").reduce((s, f) => s + parseFloat(f.amount), 0);
  const hasLateFees = fees.some((f) => f.lateFeeAmount > 0);

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">{t("finance.fees")}</h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="card">
          <div className="text-sm text-slate-500">Saldo Pendiente</div>
          <div className="text-3xl font-bold mt-1 text-red-600">{formatCurrency(outstanding)}</div>
          {hasLateFees && <div className="text-xs text-slate-500 mt-1">{t("finance.lateFeeNote")}</div>}
        </div>
        <div className="card">
          <div className="text-sm text-slate-500">Total Pagado</div>
          <div className="text-3xl font-bold mt-1 text-green-600">{formatCurrency(paid)}</div>
        </div>
      </div>

      <div className="card p-0 overflow-x-auto">
        {isError ? (
          <div className="p-6"><QueryError /></div>
        ) : isLoading ? (
          <div className="p-6 text-slate-500">{t("common.loading")}</div>
        ) : fees.length === 0 ? (
          <div className="p-6 text-slate-500">—</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-4 py-2">{t("finance.period")}</th>
                <th className="text-right px-4 py-2">{t("finance.amount")}</th>
                <th className="text-left px-4 py-2">{t("finance.dueDate")}</th>
                <th className="text-left px-4 py-2">{t("finance.status")}</th>
                <th className="text-left px-4 py-2">{t("finance.paymentMethod")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {fees.map((f) => (
                <tr key={f.id} className="hover:bg-slate-50">
                  <td className="px-4 py-2">{f.period}</td>
                  <td className="px-4 py-2 text-right font-medium">
                    {formatCurrency(f.amount)}
                    {f.status === "PARTIAL" && (
                      <div className="text-xs text-amber-600 font-normal">
                        {t("finance.paidOf", { paid: formatCurrency(f.amountPaid), total: formatCurrency(f.amount) })}
                      </div>
                    )}
                    {f.lateFeeAmount > 0 && (
                      <div className="text-xs text-red-600 font-normal">
                        + {formatCurrency(f.lateFeeAmount)} {t("finance.lateFeeShort", { pct: Math.round(f.lateFeeRate * 100) })}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2">{formatDateOnly(f.dueDate, i18n.language)}</td>
                  <td className="px-4 py-2">
                    <Badge variant={statusVariant[f.status]}>{t(`finance.${f.status.toLowerCase()}`)}</Badge>
                    {f.status !== "PAID" && f.lastReminderAt && (
                      <div className="mt-1 text-xs text-slate-500">
                        {t("finance.lastReminder", { date: formatDate(f.lastReminderAt, i18n.language) })}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2 text-slate-600">{f.paymentMethod ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
